/**
 * The SignalR push `discoveryJobChanged` (ADAPTIVE_ONBOARDING_CONTRACT.md §7; P0-07 DiscoveryJobSignal): a discovery job
 * of this workspace changed its status or its stage (claimed, each stage, each moment write, terminal).
 *
 * A trigger, never a payload: it carries ids, the status and the stage only (no names, no counts of content, no text;
 * pinned by the server test `Signal_is_content_free`). Consumers re-read `DiscoveryJobs.GetCurrent`, which is also the
 * polling fallback. DiscoveryJobStatusService is the one consumer; UserSignalrService (P0-13-b) forwards each push to
 * `DiscoveryJobStatusService.acceptSignal`, outside Angular's zone.
 *
 * The server sends it with `ISignalRUserManager.SendToTenantAsync` to the workspace's tenant-isolation group, so every
 * signed-in user of the workspace receives it; `acceptDiscoveryJobSignal` is the second, client-side tenant guard
 * (conversation-signal.ts pattern).
 *
 * NSwag generates nothing for SignalR payloads (no endpoint returns them), so the C# `DiscoveryJobChangedSignalDto`
 * (System.Text.Json: camelCase members, enum values as numbers, the date as an ISO string) is mirrored here by hand. The
 * named constants are the generated enum members (conversation-enums.ts precedent), so a renumbered C# enum fails the
 * build instead of drifting.
 */
import { DiscoveryEntryPoint, DiscoveryJobStatus, DiscoveryStage } from '@shared/service-proxies/service-proxies';

/** The hub method name. Must match `OnboardingConsts.DiscoveryJobChangedEvent` on the server. */
export const DISCOVERY_JOB_CHANGED_EVENT = 'discoveryJobChanged';

/** `DiscoveryEntryPoint`: the onboarding scan or the Organization Intelligence scan of an existing workspace. */
export const DISCOVERY_ENTRY_POINT = {
    onboarding: DiscoveryEntryPoint._1,
    intelligence: DiscoveryEntryPoint._2,
} as const;

/**
 * `DiscoveryJobStatus`. `none` (0) is only ever read (GetCurrent with no job), never stored. "Active" means queued or
 * running; completed, partial, cancelled and failed are terminal.
 */
export const DISCOVERY_JOB_STATUS = {
    none: DiscoveryJobStatus._0,
    queued: DiscoveryJobStatus._1,
    running: DiscoveryJobStatus._2,
    completed: DiscoveryJobStatus._3,
    partial: DiscoveryJobStatus._4,
    cancelled: DiscoveryJobStatus._5,
    failed: DiscoveryJobStatus._6,
} as const;

/** `DiscoveryStage`, in scan order. Only the server moves a job from one stage to the next. */
export const DISCOVERY_STAGE = {
    none: DiscoveryStage._0,
    checkingAccess: DiscoveryStage._1,
    readingConversations: DiscoveryStage._2,
    findingPeople: DiscoveryStage._3,
    connectingRelationships: DiscoveryStage._4,
    findingClients: DiscoveryStage._5,
    lookingForIdentifiers: DiscoveryStage._6,
    reconstructingWork: DiscoveryStage._7,
    lookingForPatterns: DiscoveryStage._8,
    compiling: DiscoveryStage._9,
} as const;

/**
 * The push's argument: the camelCase mirror of `DiscoveryJobChangedSignalDto`, exactly its six members.
 */
export interface DiscoveryJobChangedSignal {
    /** The workspace of the job. */
    tenantId: number;
    /** The job (always a real job: 1 or more). */
    jobId: number;
    /** The job's entry point: a `DISCOVERY_ENTRY_POINT` value. */
    entryPoint: DiscoveryEntryPoint;
    /** The job's status at the change. */
    status: DiscoveryJobStatus;
    /** The job's stage at the change. */
    stage: DiscoveryStage;
    /** When the server sent it, UTC, as the ISO string the hub delivers (not parsed here). */
    asOfUtc: string;
}

/** Whether a status is active: the job is queued or running, so its status can still change. */
export function isActiveJobStatus(status: DiscoveryJobStatus | null | undefined): boolean {
    return status === DISCOVERY_JOB_STATUS.queued || status === DISCOVERY_JOB_STATUS.running;
}

/** Whether a status is terminal: completed, partial, cancelled or failed. `none` and the active ones are not. */
export function isTerminalJobStatus(status: DiscoveryJobStatus | null | undefined): boolean {
    return (
        status === DISCOVERY_JOB_STATUS.completed ||
        status === DISCOVERY_JOB_STATUS.partial ||
        status === DISCOVERY_JOB_STATUS.cancelled ||
        status === DISCOVERY_JOB_STATUS.failed
    );
}

/** Whether a value is one of the two entry points. */
export function isDiscoveryEntryPoint(value: number | null | undefined): value is DiscoveryEntryPoint {
    return value === DISCOVERY_ENTRY_POINT.onboarding || value === DISCOVERY_ENTRY_POINT.intelligence;
}

/**
 * Whether this tab forwards a `discoveryJobChanged` push to DiscoveryJobStatusService. Drops:
 *  - no payload;
 *  - a session with no workspace (a host user) or a push for another workspace (the second guard: the server sends to
 *    this workspace's group only);
 *  - a push that names no real job or no known entry point (a malformed push; GetCurrent stays the source of truth).
 * Every other push is accepted, whoever's tab caused the change: the push is content-free and the consumer re-reads.
 *
 * @param payload the push's argument as the hub delivered it.
 * @param tenantId this session's workspace (`AppSessionService.tenantId`; null for a host user).
 */
export function acceptDiscoveryJobSignal(
    payload: DiscoveryJobChangedSignal | null | undefined,
    tenantId: number | null | undefined
): boolean {
    if (!payload) {
        return false;
    }
    if (!tenantId || payload.tenantId !== tenantId) {
        return false;
    }
    if (!(payload.jobId > 0)) {
        return false;
    }
    return isDiscoveryEntryPoint(payload.entryPoint);
}
